import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Stream from 'flarum/common/utils/Stream';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import SpeciesGalleryModal from './SpeciesGalleryModal';
import speciesListFilter from '../utils/speciesListFilter';

/** GET /api/champis-species/gallery-target - see SpeciesGalleryTargetService. */

/**
 * Inline slideshow at the top of the species page - one photo at a time
 * with previous/next navigation, "par {author}" + a link to the source
 * post, mirroring champis.net's own species-page gallery (a carousel, not
 * a thumbnail grid). Clicking the photo (or the expand button) opens
 * SpeciesGalleryModal for a larger, fullscreen view of the same photo set.
 *
 * Below it, "Ajouter des photos à la galerie" (champis.net's own button):
 * a reply in the species' gallery discussion, or - before it has one - a
 * new discussion in the right gallery, titled and tagged with the valid
 * species (SpeciesGalleryTargetService decides which). When the species
 * has several gallery discussions, they're listed, with the one photos go
 * to.
 */
export default class SpeciesGallery extends Component {
  photos = null;
  target = null;
  index = 0;
  oninit(vnode) {
    super.oninit(vnode);
    this.photos = null;
    this.index = 0;
    this.load(this.attrs.gbifId, this.attrs.colId);
  }
  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    if (vnode.attrs.gbifId !== this.attrs.gbifId || vnode.attrs.colId !== this.attrs.colId) {
      this.load(vnode.attrs.gbifId, vnode.attrs.colId);
    }
  }
  load(gbifId, colId) {
    this.photos = null;
    this.target = null;
    this.index = 0;

    // Independent of the photos: without it the page just has no button.
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/champis-species/gallery-target',
      params: colId ? {
        gbifId,
        colId
      } : {
        gbifId
      }
    }).then(target => {
      this.target = target;
      m.redraw();
    }).catch(() => {});
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/species-gallery',
      params: {
        filter: speciesListFilter(gbifId, colId)
      }
    }).then(response => {
      this.photos = (response.data || []).map(item => ({
        id: item.id,
        ...item.attributes
      }));
      m.redraw();
    }).catch(() => {
      this.photos = [];
      m.redraw();
    });
  }
  view() {
    if (this.photos === null) {
      return m(LoadingIndicator, null);
    }
    const actions = this.actions();
    if (this.photos.length === 0) {
      return actions && m("div", {
        className: "SpeciesGallery"
      }, actions);
    }
    const photos = this.photos;
    const photo = photos[this.index];
    return m("div", {
      className: "SpeciesGallery"
    }, m("div", {
      className: "SpeciesGallery-viewer"
    }, m(Button, {
      icon: "fas fa-chevron-left",
      className: "Button Button--icon SpeciesGallery-nav SpeciesGallery-nav--prev",
      onclick: () => this.previous(),
      disabled: photos.length < 2,
      "aria-label": app.translator.trans('champis-species.forum.gallery.previous')
    }), m("div", {
      className: "SpeciesGallery-image",
      role: "button",
      tabIndex: 0,
      onclick: () => this.openFullscreen(),
      onkeydown: e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.openFullscreen();
        }
      }
    }, m("img", {
      src: photo.url,
      alt: photo.baseName
    })), m(Button, {
      icon: "fas fa-chevron-right",
      className: "Button Button--icon SpeciesGallery-nav SpeciesGallery-nav--next",
      onclick: () => this.next(),
      disabled: photos.length < 2,
      "aria-label": app.translator.trans('champis-species.forum.gallery.next')
    })), m("div", {
      className: "SpeciesGallery-caption"
    }, m("span", {
      className: "SpeciesGallery-count"
    }, this.index + 1, " / ", photos.length), photo.authorName && m("span", null, app.translator.trans('champis-species.forum.gallery.by', {
      name: photo.authorName
    })), photo.discussionId && m("a", {
      href: this.sourceUrl(photo)
    }, app.translator.trans('champis-species.forum.gallery.source')), m(Button, {
      icon: "fas fa-expand",
      className: "Button Button--icon SpeciesGallery-fullscreen",
      onclick: () => this.openFullscreen(),
      "aria-label": app.translator.trans('champis-species.forum.gallery.fullscreen')
    })), actions);
  }

  /**
   * The button, when there's somewhere to add photos and the actor may (a
   * guest still sees it: the click asks them to log in), and the species'
   * gallery discussions when it has several.
   */
  actions() {
    const target = this.target;
    if (!target) return null;
    const canAct = allowed => !app.session.user || allowed;
    const button = target.reply && canAct(target.reply.allowed) || target.start && canAct(target.start.allowed) ? m(Button, {
      className: "Button",
      icon: "fas fa-camera",
      onclick: () => this.addPhotos()
    }, app.translator.trans('champis-species.forum.gallery.add_photos')) : null;

    // Several galleries often share a title (the species' name): each with
    // its opening date, the one photos go to marked.
    const list = target.galleries.length > 1 ? m("div", {
      className: "SpeciesGallery-galleries"
    }, app.translator.trans('champis-species.forum.gallery.several_galleries', {
      count: target.galleries.length
    }), m("ul", null, target.galleries.map(g => m("li", {
      key: g.id
    }, m(Link, {
      href: app.route('discussion', {
        id: `${g.id}-${g.slug}`
      })
    }, g.title), ' (', app.translator.trans('champis-species.forum.gallery.opened_on', {
      date: g.createdAt
    }), !g.validSpecies && m('[', null, ", ", app.translator.trans('champis-species.forum.gallery.synonym_gallery')), ')', g.id === target.reply?.discussionId && m("strong", null, " ", app.translator.trans('champis-species.forum.gallery.photos_go_here')))))) : null;
    return button || list ? m('[', null, button && m("div", {
      className: "SpeciesGallery-actions"
    }, button), list) : null;
  }
  addPhotos() {
    if (!app.session.user) {
      app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
      return;
    }
    const {
      reply,
      start
    } = this.target;
    if (reply) {
      // The discussion's last post, with the reply composer open there.
      app.store.find('discussions', String(reply.discussionId)).then(discussion => {
        m.route.set(app.route.discussion(discussion, discussion.lastPostNumber() ?? undefined));
        // As core's own reply button: a rejection (e.g. no longer allowed)
        // just leaves the discussion open, without the composer.
        DiscussionControls.replyAction.call(discussion, true, false).catch(() => {});
      });
    } else if (start) {
      // As RandomDescriptions' newDescriptionAction(): tags set before the
      // composer shows, or the category picker opens.
      const tag = id => app.store.getById('tags', String(id)) ?? app.store.find('tags', String(id));
      Promise.all(start.tagIds.map(tag)).then(tags => app.composer.load(() => flarum.reg.asyncModuleImport('flarum/forum/components/DiscussionComposer'), {
        user: app.session.user
      }).then(loaded => {
        // Not over a draft its author chose to keep.
        if (!loaded) return;
        app.composer.fields.tags = tags;
        app.composer.fields.title = Stream(start.title);
        app.composer.fields.species = [start.species];
        app.composer.show();
      }));
    }
  }
  sourceUrl(photo) {
    return photo.postNumber && photo.postNumber > 1 ? app.route('discussion.near', {
      id: photo.discussionId,
      near: photo.postNumber
    }) : app.route('discussion', {
      id: photo.discussionId
    });
  }
  openFullscreen() {
    app.modal.show(SpeciesGalleryModal, {
      photos: this.photos,
      index: this.index
    });
  }
  previous() {
    const count = this.photos.length;
    this.index = (this.index - 1 + count) % count;
  }
  next() {
    const count = this.photos.length;
    this.index = (this.index + 1) % count;
  }
}
flarum.reg.add('champis-species', 'forum/components/SpeciesGallery', SpeciesGallery);